<script setup>
import { ref, computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { getBaseUrl } from '@/helpers/fetch-wrapper'
import { useAuthStore } from '@/stores/auth.store'
const { user } = useAuthStore()
const { t } = useI18n()
const audiomanager = inject('audiomanager')

const props = defineProps({
  gameState: {
    type: Object,
    required: true
  },
  enable_audio: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits([
  'game-action',
  'toggle-audio',
  'chat-with-tutor',
  'example-sentences'
])

const showControls = ref(false)
const showActions = ref(true)
const currentConcept = computed(() => props.gameState.current_concept)
const concept = currentConcept.value

function getAudioUrl() {
  return getBaseUrl(`/tts/?lang=${concept.learning.language}&text=${concept.learning.pronunciation}&token=${user.token}`)
}
audiomanager.setPlaybackSpeed(1)
audiomanager.play(getAudioUrl())

// Play the audio of the learning language option

function playAudio(slow = false) {
  if (!props.enable_audio) return
  const concept = currentConcept.value
  if (!concept?.learning?.pronunciation) return
  const url = getAudioUrl()
  audiomanager.setPlaybackSpeed(slow ? 0.5 : 1)
  audiomanager.play(url)
}

function choose(action) {
  showActions.value = false
  emit('game-action', {
    correct: action === 'skip',
    auto_advance: true, // don't play a sound or make the user choose next, just go to the next concept
  })
}

function chatWithTutor() {
  showControls.value = false
  emit('chat-with-tutor')
}

function showExampleSentences() {
  showControls.value = false
  emit('example-sentences')
}

function toggleAudio() {
  emit('toggle-audio')
}
</script>

<template>
  <div class="game-container border p-3 shadow rounded-4 mt-2 text-center">
    <div class="me-0" style="float: right">
      <button class="btn-grear" @click="showControls = !showControls">
        <i class="bi bi-gear"></i>
      </button>
      <div v-if="showControls" class="popout-menu">
        <ul class="text-start">
          <li @click="chatWithTutor()">
            <i class="bi bi-chat-dots me-1"></i>
            {{ t('course.chat-with-tutor') }}
          </li>
          <li @click="showExampleSentences()">
            <i class="bi bi-translate me-1"></i>
            {{ t('curriculum.example-sentences') }}
          </li>
          <li @click="toggleAudio()">
            <i :class="props.enable_audio ? 'bi bi-volume-up-fill me-1' : 'bi bi-volume-mute-fill me-1'"></i>
            {{ props.enable_audio ? t('game.audio-on') : t('game.audio-off') }}
          </li>
          <li @click="playAudio(false)">
            <i class="bi bi-play-circle me-1"></i>
            {{ t('curriculum.play-again') }}
          </li>
          <li @click="playAudio(true)">
            <i class="bi bi-play-circle me-1"></i>
            {{ t('curriculum.play-slow') }}
          </li>
        </ul>
      </div>
    </div>
    <h2 class="mb-2" v-html="currentConcept.learning.annotated.annotation"></h2>
    <div class="mb-2">
      <span class="fs-4 text-muted">{{ currentConcept.native.text }}</span>
    </div>
    <div class="mb-3">
      <button class="btn btn-outline-primary me-2" @click="chatWithTutor()">
        <i class="bi bi-chat"></i> {{ t('curriculum.chat-with-tutor') }}
      </button>
      <button class="btn btn-outline-primary me-2" @click="playAudio()">
        <i class="bi bi-volume-up"></i> {{ t('curriculum.play-again') }}
      </button>
    </div>
    <div v-if="showActions">
      <button class="btn btn-success me-2" @click="choose('learn')">
        <i class="bi bi-check-circle"></i> {{ t('game.learn-this') }}
      </button>
      <button class="btn btn-secondary" @click="choose('skip')">
        <i class="bi bi-skip-forward"></i> {{ t('game.skip') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.game-container {
  max-width: 600px;
  margin: 0 auto;
  margin-bottom: 1rem;
}
.popout-menu {
  z-index: 1000;
  position: fixed;
  background: white;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transform: translateX(-80%);
  width: 250px;
}
.popout-menu ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.popout-menu li {
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 1px solid #ccc;
  font-size:14px;
  font-weight:500;
}
.popout-menu li:hover {
  background-color: #f1f1f1;
}
.btn-grear{
  background: transparent;
  padding: 5px 10px;
}
</style>